import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const medicalSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Medical Facility Cleaning Services',
  description: 'Specialized medical facility cleaning in Louisville, KY. Hospital-grade disinfection, HIPAA-compliant staff, infection control, biohazard handling, and exam room sanitization.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Medical Facility Cleaning',
};

export default function MedicalFacilityCleaningPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 10); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'ri-shield-cross-line',
      title: 'Medical-Grade Disinfection',
      description: 'Hospital-grade disinfectants and EPA-approved cleaning agents that eliminate pathogens, bacteria, and viruses to maintain the highest sanitation standards.'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Biohazard Handling',
      description: 'Proper disposal and cleaning protocols for biohazardous materials following OSHA regulations and healthcare industry standards.'
    },
    {
      icon: 'ri-file-shield-line',
      title: 'HIPAA Compliance',
      description: 'Our staff is trained in HIPAA regulations to protect patient privacy and confidentiality while performing cleaning duties in healthcare settings.'
    },
    {
      icon: 'ri-virus-line',
      title: 'Infection Control',
      description: 'Specialized cleaning techniques focused on preventing healthcare-associated infections through proper sanitization of high-touch surfaces and equipment.'
    },
    {
      icon: 'ri-stethoscope-line',
      title: 'Exam Room Sanitization',
      description: 'Thorough cleaning and disinfection of examination rooms, medical equipment surfaces, and patient contact areas between appointments.'
    },
    {
      icon: 'ri-hospital-bed-line',
      title: 'Patient Area Care',
      description: 'Gentle yet effective cleaning of patient rooms, waiting areas, and common spaces with attention to patient comfort and safety.'
    }
  ];

  const benefits = [
    'Certified cleaning staff with healthcare facility training',
    'Compliance with CDC and OSHA guidelines',
    'Color-coded cleaning systems to prevent cross-contamination',
    'Detailed cleaning logs and quality assurance documentation',
    'Emergency response cleaning services available',
    'Specialized equipment for healthcare environments'
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SEOHead
        title="Medical Facility Cleaning Louisville KY | First Insight Cleaning"
        description="Specialized medical facility cleaning in Louisville, KY. Hospital-grade disinfection, HIPAA-compliant staff, infection control &amp; biohazard handling. CDC &amp; OSHA compliant. Request a free quote."
        keywords="medical facility cleaning Louisville KY, healthcare janitorial services Kentucky, hospital cleaning, First Insight Cleaning"
        canonical={`${siteUrl}/services/medical-facility-cleaning`}
        schemaJson={medicalSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ae7c418e3916cb937bd359de691dc140.jpg"
            alt="Professional Medical Facility Cleaning Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Poppins']">
            Medical Facility Cleaning
          </h1>
          <p className="text-base md:text-xl text-slate-100 mb-8 leading-relaxed">
            Specialized cleaning services for healthcare environments with strict sanitation protocols, medical-grade disinfectants, and trained professionals who understand the critical importance of infection control.
          </p>
          <Link
            to="/#quote"
            className="inline-block bg-teal-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Healthcare Cleaning Excellence
            </h2>
            <div className="w-16 h-1 bg-teal-500 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Medical facilities require a level of cleanliness that goes far beyond standard commercial cleaning. Patient safety, infection prevention, and regulatory compliance depend on meticulous attention to detail and specialized cleaning protocols.
                </p>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Our medical facility cleaning team undergoes extensive training in healthcare sanitation standards, bloodborne pathogen protocols, and proper use of medical-grade disinfectants. We understand the unique challenges of healthcare environments and the critical role cleanliness plays in patient outcomes.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  From small clinics to large medical centers, we provide comprehensive cleaning services that meet or exceed industry standards. Our systematic approach ensures consistent results, proper documentation, and peace of mind for healthcare administrators and staff.
                </p>
              </div>
              <div className="w-full h-[400px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_5e88ef9dff23fb125ea75d9276f310bc_425898a0.jpg"
                  alt="Medical Facility Cleaning in Action"
                  className="w-full h-full object-cover object-center rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-slate-800 mb-12 text-center font-['Poppins']">
              Specialized Healthcare Cleaning Services
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-slate-100"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-500 rounded-lg mb-4">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-teal-50 rounded-2xl p-12">
            <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center font-['Poppins']">
              Why Healthcare Facilities Trust Us
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-500"></i>
                  </div>
                  <p className="text-slate-700 text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Protect Your Patients with Professional Cleaning
            </h3>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Contact us to discuss your medical facility's unique cleaning needs and learn how we can help maintain the highest standards of cleanliness and safety.
            </p>
            <Link
              to="/#quote"
              className="inline-block bg-teal-500 text-white px-10 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
